"use client";

import { useMemo } from "react";
import { useRouter } from "next/navigation";
import { type LucideIcon } from "lucide-react";

import { FeedItemCard } from "@/components/feed/feed-item-card";
import { SectionHeader } from "@/components/feed/section-header";
import {
  usePaginatedFeed,
  type FeedFilterState,
  type ListingItem,
} from "@/hooks/use-paginated-feed";

/**
 * ItemCardRail — a horizontal strip of the shared FeedItemCard on the landing
 * (mockup rails: Trending now, Open to offers, Live auctions…). Reuses the feed
 * data + card so it matches the grid. Optionally scope to listing formats
 * (server filter) and/or a client `filter` (e.g. auctions ending soon). The
 * whole section collapses once we know it's empty.
 */
export function ItemCardRail({
  title,
  icon,
  currentUserId,
  listingFormats,
  filter,
  limit = 12,
  onSeeAll,
  className,
}: {
  title: string;
  icon: LucideIcon;
  currentUserId?: string;
  listingFormats?: string[];
  filter?: (listing: ListingItem) => boolean;
  limit?: number;
  onSeeAll?: () => void;
  className?: string;
}) {
  const router = useRouter();
  const formatKey = listingFormats?.join(",") ?? "";
  const filters = useMemo<FeedFilterState | undefined>(
    () =>
      formatKey
        ? {
            brands: [],
            themes: [],
            categories: [],
            conditions: [],
            gradeRange: null,
            listingFormats: formatKey.split(","),
          }
        : undefined,
    [formatKey],
  );
  // Over-fetch when a client filter narrows the set, so the rail still fills.
  const fetchCount = filter ? limit * 3 : limit;
  const { items, isInitialized } = usePaginatedFeed({
    userId: currentUserId,
    mode: "just-listed",
    limit: fetchCount,
    maxItems: fetchCount,
    filters,
  });

  const shown = (filter ? items.filter(filter) : items).slice(0, limit);
  if (isInitialized && shown.length === 0) return null;

  return (
    <div className={className}>
      <SectionHeader icon={icon} title={title} onSeeAll={onSeeAll} />
      <div className="hide-scrollbar -mx-1 flex gap-5 overflow-x-auto px-1 pb-1.5">
        {isInitialized
          ? shown.map((listing) => (
              <div key={listing.id} className="w-[214px] shrink-0">
                <FeedItemCard
                  listing={listing}
                  currentUserId={currentUserId}
                  onOpen={(id) => router.push(`/listings/${id}`)}
                />
              </div>
            ))
          : Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="w-[214px] shrink-0">
                <div className="aspect-[4/5] w-full animate-pulse rounded-2xl bg-muted" />
              </div>
            ))}
      </div>
    </div>
  );
}
